import React, { useEffect, useRef } from 'react';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/material';
import classNames from 'classnames';
import variables from '../../../styles/variables';
import theme from '../../../styles/theme';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
  },

  formats: {
    display: 'grid',
    grid: 'auto / repeat(3, 1fr)',
    rowGap: 16,
    columnGap: 16,
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column',
      grid: 'auto / repeat(2, 1fr)',
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      grid: 'auto / repeat(1, 1fr)',
    },
    '& .format': {
      width: '286px',
      backgroundColor: variables.composerBackgroundGrey,
      padding: '32px',
      borderRadius: '20px',

      '&.pip-in-viewport': {
        '& .content': {
          animation: '$pip 6s ease-in-out forwards',
        },
      },

      '&.reveal-in-viewport': {
        '& .content': {
          animation: '$reveal 6s ease-in-out 0s forwards',
        },
      },

      '&.overlay-in-viewport': {
        '& .creative': {
          animation: '$overlay 6s ease-in-out 0s forwards',
        },
      },

      '&.reveal': {
        '& .content': {
          transformOrigin: '282px top',
        },
      },

      '&.overlay': {
        '& .creative': {
          zIndex: 200,
          opacity: 0,
          width: '247px',
          height: '31px',
          fontSize: '13px',
          position: 'absolute',
          left: '21px',
          top: '133px',
        },
      },

      '& .name': {
        fontWeight: '700',
        fontSize: '26px',
        color: variables.composerBlack,
      },
      '& .desc': {
        height: '65px',
        margin: '10px 0px 0 0',
        fontWeight: '400',
        fontSize: '14px',
        lineHeight: '21px',
        color: variables.composerBlack,
      },
      '& .screen': {
        margin: '30px 0px 0 0',
        width: '290px',
        height: '176px',
        border: `1px solid ${variables.composerBorderGrey}`,
        borderRadius: '10px',
        position: 'relative',
        background: `repeating-linear-gradient(-45deg, transparent 0 5px,${variables.composerLightPurple} 5px 6px)`,

        '& .content': {
          display: 'flex',
          border: `2px solid ${variables.composerBlack}`,
          borderRadius: '10px',
          width: '290px',
          height: '174px',
          position: 'relative',
          backgroundColor: variables.white,
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '16px',
          fontWeight: '500',
          zIndex: 100,
        },
        '& .creative': {
          display: 'flex',
          border: `2px solid ${variables.composerMarketingPurple}`,
          borderRadius: '10px',
          width: '154px',
          height: '87px',
          position: 'absolute',
          top: '42px',
          left: '125px',
          backgroundColor: variables.composerVeryLightPurple,
          color: variables.composerMarketingPurple,
          fontSize: '13px',
          fontWeight: '500',
          justifyContent: 'center',
          alignItems: 'center',
        },
        '& .desc': {
          position: 'absolute',
          top: 134,
          left: 75,
          fontSize: '13px',
          fontWeight: '500',
          color: variables.composerMarketingPurple,
        },
      },
    },
    '& .last': {
      margin: '0 0px 0 0',
    },
  },

  '@keyframes pip': {
    '0%': {
      transform: 'scale(1.0)',
      left: '0%',
    },
    '20%': {
      transform: 'scale(0.36)',
      left: '-29.5%',
      borderWidth: '5px',
      borderRadius: '28px',
      fontSize: '32px',
    },
    '40%': {
      transform: 'scale(0.36)',
      left: '-29.5%',
      borderWidth: '5px',
      borderRadius: '28px',
      fontSize: '32px',
    },
    '100%': {
      transform: 'scale(0.36)',
      left: '-29.5%',
      borderWidth: '5px',
      borderRadius: '28px',
      fontSize: '32px',
    },
  },

  '@keyframes reveal': {
    '0%': {
      transform: 'scale(1.0)',
    },
    '20%': {
      transform: 'scale(0.78)',
      borderRadius: '11px',
      fontSize: '22px',
    },
    '40%': {
      transform: 'scale(0.78)',
      borderRadius: '11px',
      fontSize: '22px',
    },
    '100%': {
      transform: 'scale(0.78)',
      borderRadius: '11px',
      fontSize: '22px',
    },
  },

  '@keyframes overlay': {
    '0%': {
      transform: 'scale(0)',
      opacity: 0,
    },
    '20%': {
      transform: 'scale(1)',
    },
    '30%': {
      transform: 'scale(1)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 1,
    },
  },
}));

const Formats = () => {
  const classes = useStyles();

  const pipTarget = useRef(null);
  const revealTarget = useRef(null);
  const overlayTarget = useRef(null);

  const getFormat = (entry) => {
    let format = null;
    if (entry.target.classList.contains('reveal')) {
      format = 'reveal';
    } else if (entry.target.classList.contains('pip')) {
      format = 'pip';
    } else if (entry.target.classList.contains('overlay')) {
      format = 'overlay';
    }

    return format;
  };

  const handleIntersection = (entries) => {
    entries.map((entry) => {
      const format = getFormat(entry);

      if (entry.isIntersecting) {
        entry.target.classList.add(`${format}-in-viewport`);
      }

      return null;
    });
  };

  const handleOutOfView = (entries) => {
    entries.map((entry) => {
      const format = getFormat(entry);

      if (!entry.isIntersecting) {
        entry.target.classList.remove(`${format}-in-viewport`);
      }
      return null;
    });
  };

  useEffect(() => {
    const currentPipTarget = pipTarget.current;
    const currentRevealTarget = revealTarget.current;
    const currentOverlayTarget = overlayTarget.current;

    const observer = new IntersectionObserver(handleIntersection, { threshold: 1 });
    observer.observe(currentPipTarget);
    observer.observe(currentRevealTarget);
    observer.observe(currentOverlayTarget);

    const outOfViewObserver = new IntersectionObserver(handleOutOfView, { threshold: 0.5 });
    outOfViewObserver.observe(currentPipTarget);
    outOfViewObserver.observe(currentRevealTarget);
    outOfViewObserver.observe(currentOverlayTarget);

    return () => {
      observer.unobserve(currentPipTarget);
      observer.unobserve(currentRevealTarget);
      observer.unobserve(currentOverlayTarget);

      outOfViewObserver.unobserve(currentPipTarget);
      outOfViewObserver.unobserve(currentRevealTarget);
      outOfViewObserver.unobserve(currentOverlayTarget);
    };
  }, []);

  return (
    <Box className={classes.container}>
      <Box className={classes.formats}>
        <Box className={classNames('format', 'pip')} ref={pipTarget}>
          <Box className="name">Picture-in-Picture</Box>
          <Box className="desc">
            Scale down content side-by-side with a video ad, complemented by branded background graphics.
          </Box>
          <Box className="screen">
            <Box className="creative">Creative</Box>
            <Box className="content">Content</Box>
            <Box className="desc">Branded background</Box>
          </Box>
        </Box>
        <Box className={classNames('format', 'reveal')} ref={revealTarget}>
          <Box className="name">Squeeze Back</Box>
          <Box className="desc">Introduce a personalized L-Bar canvas that can be customized and animated.</Box>
          <Box className="screen">
            <Box className="content">Content</Box>
            <Box className="desc">Branded background</Box>
          </Box>
        </Box>
        <Box className={classNames('format', 'overlay', 'last')} ref={overlayTarget}>
          <Box className="name">Overlay</Box>
          <Box className="desc">Feature a branded graphic directly over the primary content stream.</Box>
          <Box className="screen">
            <Box className="creative">Creative</Box>
            <Box className="content">Content</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Formats;
